/**
 * The WebCrypto seam for chat share SNAPSHOT payloads.
 *
 * A snapshot share (`buildSnapshotPayload`) is the one artifact in the product
 * that carries request credentials as plain jsonb in `share_links.payload`,
 * while chat message bodies, workspaces and collections are all encrypted at
 * rest. This module closes that: the payload leaves the device as an ENVELOPE —
 * a small plaintext skeleton plus one AES-256-GCM ciphertext — encrypted under
 * the same per-channel key chat attachments use (`chatCrypto`), in the same
 * wire format (12-byte random IV prepended to the ciphertext).
 *
 * THIS IS NOT END-TO-END ENCRYPTION, and must not be described as such.
 *
 * The key is `chat_channels.enc_key`: 32 bytes minted by the Apiboo backend and
 * handed to any account that is a member of the channel at the moment it asks
 * (socket join reply, or `GET /chat/channels/:id/key`). There is no key
 * exchange, no per-user keypair, nothing is verified, and nothing is ever
 * rotated. So, accurately:
 *
 *   - Everyone in the conversation can decrypt a share. So can anyone ADDED to
 *     the conversation tomorrow — they get the same key and it opens shares
 *     sent before they arrived. Encryption does not change the audience.
 *   - Apiboo holds the key and can read every share.
 *   - What it DOES change is what our storage holds: the payload column, its
 *     backups, replicas, `psql` sessions and any Sentry event that captures the
 *     request body stop containing the credential. That is a real reduction in
 *     OUR exposure, and it is the only claim the copy is allowed to make.
 *
 * It is therefore NOT a replacement for redaction (`utils/shareRedaction.js`).
 * Redaction decides what the RECIPIENT gets; this decides what the DATABASE
 * holds. Under a channel key those two sets do not overlap at all.
 *
 * ── Why an envelope and not one opaque blob ──────────────────────────────────
 *
 * An image can be an opaque blob because the server never reads one. A share
 * payload is read server-side in four places, and three of them need a
 * skeleton:
 *
 *   S1  create-time shape gate — `payload` must carry a `request`/`collection`/
 *       `workspace` MAP, and `requests`/`collections` must be lists of MAPS.
 *   S2  accept card title — `payload["name"]`, `["description"]`, and for a
 *       request share `payload["request"]["method"]`.
 *   S3  accept card preview — per-request `name`/`method` for a collection
 *       share; per-collection `name` and `length(requests)` for a workspace
 *       share.
 *   S4  cloud accept — walks the tree and copies the request fields into real
 *       rows. This one CANNOT read ciphertext, so encrypted shares are accepted
 *       client-side instead (`ShareAcceptModal`), which is the same
 *       fetch-decrypt-create shape local destinations have always used.
 *
 * Everything S1–S3 keeps in the clear is already broadcast in the clear in the
 * chat card's own `chat_messages.metadata` (`buildShareMetadata` sends the
 * label, the method and up to 30 request names/methods), in a column Cloak does
 * not cover. So the skeleton leaks nothing that was not already leaking, and it
 * needs no backend change at all. Anything the server does NOT read stays out
 * of the skeleton — that rule is pinned by a test, because a credential
 * accidentally left in the plaintext half would defeat the whole change.
 *
 * ── Versioning ──────────────────────────────────────────────────────────────
 *
 * `enc` absent means a v0 PLAINTEXT payload — every share minted before this
 * shipped. Those keep working forever and are never touched here. New shares
 * carry `enc` ("aes-gcm-256", the string the attachment path already uses), `v`
 * (this envelope's version) and `epoch` (0 — reserved for the channel-key
 * epochs designed in chat-e2e-design.md but not built; a share minted without
 * one would become silently unopenable the day rotation lands).
 *
 * Both markers are repeated on the type sub-map because `POST /share/:token/
 * data` returns ONLY that sub-map (`Shares.link_data/1`) — the accepting client
 * never sees the payload root.
 *
 * Failures are three DISTINCT codes, never spelled the same way (the
 * `vaultShareCrypto` discipline): `unsupported_version` is "this client is too
 * old", `decrypt_failed` is "the bytes did not open", `key_unavailable` is "we
 * could not get the channel key". "Could not check" must never render as
 * "nothing was wrong".
 */
import { decryptBuffer, encryptFile, getChannelCryptoKey } from './chatCrypto';

/** The `enc` marker, identical to the one chat attachments stamp. */
export const ENC_ALGORITHM = 'aes-gcm-256';
/** Envelope layout version. A payload with a HIGHER `v` must fail loudly. */
export const ENVELOPE_VERSION = 1;
/** Channel-key epoch. Rotation does not exist yet; the field does, on purpose. */
export const ENVELOPE_EPOCH = 0;

export class ShareEnvelopeError extends Error {
  constructor(code, message) {
    super(message);
    this.name = 'ShareEnvelopeError';
    this.code = code;
  }
}

/** Copy for each failure code — the recipient must be able to tell them apart. */
export function shareEnvelopeMessage(code) {
  switch (code) {
    case 'unsupported_version':
      return 'This share needs a newer version of Apiboo.';
    case 'key_unavailable':
    case 'decrypt_failed':
    default:
      return "This share can't be opened on this account.";
  }
}

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

function bytesToBase64(bytes) {
  let binary = '';
  const view = new Uint8Array(bytes);
  for (let i = 0; i < view.length; i += 1) binary += String.fromCharCode(view[i]);
  return btoa(binary);
}

function base64ToBytes(b64) {
  return Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
}

// `encryptFile`/`decryptBuffer` speak Blob, and Blob.arrayBuffer() is missing
// in jsdom 16 (and in Safari < 14) — read through FileReader when it is.
function blobToBytes(blob) {
  if (typeof blob.arrayBuffer === 'function') {
    return blob.arrayBuffer().then((buf) => new Uint8Array(buf));
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(blob);
  });
}

// A File-like for encryptFile: it only ever calls `.arrayBuffer()`, and jsdom's
// Blob does not implement that (docMediaCrypto's tests use the same shape).
function fileLike(bytes) {
  return { arrayBuffer: async () => bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) };
}

/** Encrypt any JSON-serializable value -> `{ iv, ct }`, both base64. */
async function sealJson(cryptoKey, value) {
  const blob = await encryptFile(cryptoKey, fileLike(textEncoder.encode(JSON.stringify(value))));
  const bytes = await blobToBytes(blob);
  return {
    iv: bytesToBase64(bytes.slice(0, 12)),
    ct: bytesToBase64(bytes.slice(12)),
  };
}

/** `{ iv, ct }` -> the original value. Throws `decrypt_failed` on anything. */
async function openJson(cryptoKey, { iv, ct }) {
  let plaintext;
  try {
    const ivBytes = base64ToBytes(iv);
    const ctBytes = base64ToBytes(ct);
    const joined = new Uint8Array(ivBytes.length + ctBytes.length);
    joined.set(ivBytes, 0);
    joined.set(ctBytes, ivBytes.length);
    const blob = await decryptBuffer(cryptoKey, joined.buffer, 'application/json');
    plaintext = textDecoder.decode(await blobToBytes(blob));
  } catch (e) {
    throw new ShareEnvelopeError('decrypt_failed', shareEnvelopeMessage('decrypt_failed'));
  }
  try {
    return JSON.parse(plaintext);
  } catch (e) {
    throw new ShareEnvelopeError('decrypt_failed', shareEnvelopeMessage('decrypt_failed'));
  }
}

const isPlainObject = (v) => v != null && typeof v === 'object' && !Array.isArray(v);

/** True for an envelope root (`buildSnapshotPayload` output that was sealed). */
export function isEncryptedPayload(payload) {
  return isPlainObject(payload) && typeof payload.enc === 'string';
}

/**
 * True for the sub-map `POST /share/:token/data` hands back — the accepting
 * client never sees the payload root, so the marker has to be readable here.
 */
export function isEncryptedSnapshotData(data) {
  return isPlainObject(data) && typeof data.enc === 'string';
}

const markers = () => ({ enc: ENC_ALGORITHM, v: ENVELOPE_VERSION, epoch: ENVELOPE_EPOCH });

/**
 * The plaintext skeleton for one share type — EXACTLY the fields the server
 * reads (S1–S3 above) and nothing else. Every field named here is already sent
 * in the clear in the chat card's metadata; nothing else may be added without
 * re-checking that.
 */
function skeleton(type, source) {
  if (type === 'request') {
    // S2: `payload["request"]["method"]` drives the method badge.
    return { method: source?.method || 'GET' };
  }
  if (type === 'collection') {
    // S1: `requests` must be a list of maps. S3: name + method per row, and
    // `length(requests)` is the card's request count.
    return {
      requests: (source?.requests || []).map((r) => ({
        name: r?.name || 'Untitled',
        method: (r?.method || 'GET').toUpperCase(),
      })),
    };
  }
  if (type === 'workspace') {
    // S3 reads only each collection's NAME and its request COUNT — never a
    // request's name or method — so the rows are placeholders that carry the
    // count and satisfy S1's list-of-maps check, and nothing more.
    return {
      collections: (source?.collections || []).map((c) => ({
        name: c?.name || 'Untitled',
        requests: (c?.requests || []).map(() => ({})),
      })),
    };
  }
  throw new Error(`Unsupported snapshot share type: ${type}`);
}

/**
 * Seal a `buildSnapshotPayload` result under the channel key.
 *
 * The ciphertext is the WHOLE original sub-object, so decryption returns it
 * byte-for-byte rather than reassembling it from a skeleton plus parts — the
 * skeleton is a projection for the server, never a source of truth for the
 * recipient.
 *
 * Returns the payload UNCHANGED when `channelId` is null (nothing to encrypt
 * under) — callers that cannot reach a channel key must keep working, and the
 * v0 plaintext format is exactly what they already sent.
 */
export async function encryptSnapshotPayload(channelId, type, payload) {
  if (channelId == null || payload == null) return payload;
  const source = payload[type];
  if (!isPlainObject(source)) return payload;

  let cryptoKey;
  try {
    cryptoKey = await getChannelCryptoKey(channelId);
  } catch (e) {
    throw new ShareEnvelopeError('key_unavailable', 'Could not reach this conversation’s encryption key.');
  }
  const sealed = await sealJson(cryptoKey, source);

  return {
    // S2 — the accept card's title and subtitle, already in the chat metadata.
    name: payload.name,
    description: payload.description,
    ...markers(),
    [type]: { ...markers(), ...skeleton(type, source), ...sealed },
  };
}

/**
 * Open the sub-map returned by `shareService.fetchShareData`. A v0 (plaintext)
 * share is returned untouched — mixed rows are the normal state, not an edge
 * case, and every share minted before this feature is one.
 */
export async function decryptSnapshotData(channelId, data) {
  if (!isEncryptedSnapshotData(data)) return data;

  if (data.enc !== ENC_ALGORITHM || !(Number(data.v) <= ENVELOPE_VERSION)) {
    throw new ShareEnvelopeError('unsupported_version', shareEnvelopeMessage('unsupported_version'));
  }
  if (typeof data.iv !== 'string' || typeof data.ct !== 'string') {
    throw new ShareEnvelopeError('decrypt_failed', shareEnvelopeMessage('decrypt_failed'));
  }
  if (channelId == null) {
    throw new ShareEnvelopeError('key_unavailable', shareEnvelopeMessage('key_unavailable'));
  }

  let cryptoKey;
  try {
    cryptoKey = await getChannelCryptoKey(channelId);
  } catch (e) {
    throw new ShareEnvelopeError('key_unavailable', shareEnvelopeMessage('key_unavailable'));
  }
  return openJson(cryptoKey, data);
}
